/**
 * The Post Stylizer's JSON, as a post record carries it.
 *
 * A styled post is *not* a picture. It is the ordinary post — the same words on
 * the same record, in the same feed — plus a small JSON mark that says how to
 * draw those words: a background, a colour of ink, some ranges to highlight,
 * a face and a size. The picture is made by the reader (`PostDesignRenderer`), which
 * is why the styling costs a second device nothing to see, can be read by a
 * screen reader, and never needs an export step to be part of the feed.
 *
 * The mark is read defensively, and that is the whole contract here: it arrives
 * from the network on a record written by a build that may be older or newer than
 * this one. Anything that is not a well-formed mark reads as *not styled*, and a
 * post that a newer build styled in a way this one has no field for draws as its
 * ordinary text rather than breaking the feed around it.
 *
 * `text` is written down beside the ranges for one reason: ranges are offsets,
 * and offsets only mean something against the words they were measured on. The
 * snapshot is what a reader reconciles against when the two differ — the app
 * trims a post's text on the way out (`resolveRT` drops leading blank lines and
 * trailing space), and an author may edit a styled post's words after styling
 * them. Without it, a one-word deletion would slide every highlight in the card
 * onto the wrong words, silently.
 */
import {z} from 'zod'

/**
 * The name the mark travels under, beside the post's own fields.
 *
 * A post record may carry fields the lexicon does not name — the app already
 * rides a classroom's own words this way (`classroomPostMark`) — and this is the
 * same door: `stylizer` on `app.bsky.feed.post`, ignored by every client that has
 * never heard of it, which then draws the post's ordinary text.
 */
export const STYLIZER_MARK = 'stylizer'

/**
 * How many ranges a mark may carry, of each kind.
 *
 * A limit rather than a count, so a crafted record cannot make a reader lay out
 * a thousand segments per post: a card is a sentence with a word or two marked,
 * and forty of each is beyond what a card can even show.
 */
export const STYLED_POST_MAX_RANGES = 40

/**
 * The type sizes a design accepts, in points.
 *
 * The range is what a *canvas* can hold rather than what a caption can: a designed
 * post is a poster, so its words are set large (`#/state/postStyle/layout` picks a
 * size from the words themselves), and the ceiling is where a headline stops being
 * a headline on the widest canvas the app runs on.
 */
export const STYLED_POST_MIN_SIZE = 14
export const STYLED_POST_MAX_SIZE = 72
export const STYLED_POST_DEFAULT_SIZE = 44

/** A colour as it is written in a mark: `#rrggbb`, and nothing else. */
const COLOR_RE = /^#[0-9a-fA-F]{6}$/

export const styledPostColorSchema = z.string().regex(COLOR_RE)

/**
 * A stretch of the post's own words, by offset.
 *
 * Offsets in code units of the snapshot text, which is what a `Text` and a
 * `TextInput` both index by, so a range never lands mid-character on either.
 */
const baseRangeSchema = z.object({
  start: z.number().int().min(0),
  end: z.number().int().min(1),
})

/**
 * A range that ends before it starts is not a range: it is a record some client
 * wrote wrong, and a card cannot draw it. Refusing the whole mark is the honest
 * answer — the post then draws as its own words.
 */
function isForwardRange(range: unknown): boolean {
  /*
   * Read back through the two offsets this helper's own schema owns: Zod 3 widens
   * the output of `extend` when the shape it is given is generic, so the type
   * arriving here is a record of possibly-missing keys even though `start` and
   * `end` above are required numbers and have just been validated as such.
   */
  const {start, end} = range as {start: number; end: number}
  return end > start
}

function rangeSchema<T extends z.ZodRawShape>(extra: T) {
  return baseRangeSchema.extend(extra).refine(isForwardRange, {
    message: 'a range must cover at least one character',
  })
}

export const styledPostRangeSchema = rangeSchema({})

export const styledPostHighlightSchema = rangeSchema({
  color: styledPostColorSchema,
})

/** The weights the shipped Thmanyah faces actually have (`#/state/postStyle/fonts`). */
export const styledPostWeightSchema = z.enum(['regular', 'bold', 'black'])
export type StyledPostWeight = z.infer<typeof styledPostWeightSchema>

/**
 * What is behind the card's words.
 *
 * *Solid* is a colour and needs nothing from anywhere. *Image* is a picture, and
 * the colour is kept beside it as the answer for every reader whose device cannot
 * fetch the picture in time, or at all.
 *
 * The picture is the **post's own**: a design is made over a picture the author
 * added to the post the way they add any picture — picked in the composer, shown
 * while they write, uploaded once when they publish — so a mark that names no blob
 * is read as "the post's picture", and a reader draws the very image the record
 * already carries. Nothing has to be fetched from a second place and nothing can go
 * stale when the author replaces it.
 *
 * A mark may still *name* the blob it drew (`did`/`cid`/`mimeType`): that is what
 * the first version of this feature wrote, and it is how a design whose picture has
 * since left the post keeps being drawn. A reader prefers the named blob when there
 * is one and the post's own picture when there is not. The two halves of an address
 * go together — a `cid` with no repo is a picture no reader can find, and the whole
 * mark is refused rather than drawn as a design with a hole in it.
 */
export const styledPostBackgroundSchema = z.union([
  z.object({
    type: z.literal('solid'),
    color: styledPostColorSchema,
  }),
  z
    .object({
      type: z.literal('image'),
      did: z.string().min(1).optional(),
      cid: z.string().min(1).optional(),
      mimeType: z.string().min(1).optional(),
      color: styledPostColorSchema,
    })
    .refine(background => !!background.did === !!background.cid, {
      message: 'a picture is addressed by a repo and a blob together',
    }),
])
export type StyledPostBackground = z.infer<typeof styledPostBackgroundSchema>

/**
 * The values a template leaves to its author.
 *
 * One today — the colour a template's own paths are drawn in — and written as a
 * closed shape open to a second, because the alternative (baking the colour into
 * the paths of every design that ever chose that template) would make choosing a
 * template a decision nobody could afterwards change. A reader can tell a value it
 * understands from one written by a build it does not; an unvalidated bag could not
 * be drawn at all without guessing.
 */
export const styledPostTemplateValuesSchema = z.object({
  pathColor: styledPostColorSchema.optional(),
})
export type StyledPostTemplateValues = z.infer<
  typeof styledPostTemplateValuesSchema
>

/**
 * One styled post, as it travels.
 *
 * Every field but the background is optional, because a card is only ever what
 * its author asked for: an untouched styling tool says nothing, and the reader
 * then draws the defaults in `#/state/postStyle/templates` rather than a card
 * full of fields nobody chose.
 */
export const styledPostSchema = z.object({
  /** The shape's version, so a later change can be told apart from a bad read. */
  v: z.literal(1),
  /** The words the ranges below were measured against. */
  text: z.string().max(4000),
  background: styledPostBackgroundSchema,
  /** The colour the words are printed in. Defaults to the template's own ink. */
  ink: styledPostColorSchema.optional(),
  /** Whether a grain is laid over the background. */
  texture: z.boolean().optional(),
  /** The colour the *highlight* control paints with, remembered per card. */
  highlightColor: styledPostColorSchema.optional(),
  weight: styledPostWeightSchema.optional(),
  size: z
    .number()
    .min(STYLED_POST_MIN_SIZE)
    .max(STYLED_POST_MAX_SIZE)
    .optional(),
  align: z.enum(['start', 'center']).optional(),
  highlights: z
    .array(styledPostHighlightSchema)
    .max(STYLED_POST_MAX_RANGES)
    .optional(),
  /** The words wearing the Mursala elongation, as ranges. */
  mursalas: z
    .array(styledPostRangeSchema)
    .max(STYLED_POST_MAX_RANGES)
    .optional(),
  /** The template this card was built from, when it was built from one. */
  template: z.string().max(24).optional(),
  /** The ornament a template places on the card. */
  sticker: z.string().max(24).optional(),
  /** What the author changed *in* the template (`styledPostTemplateValuesSchema`). */
  templateValues: styledPostTemplateValuesSchema.optional(),
})

export type StyledPost = z.infer<typeof styledPostSchema>

/**
 * The styling a post's record says it has, when it says so at all.
 *
 * Read defensively and answered as `null` for anything else: a mark written by a
 * newer build, a truncated one, a string where an object was expected. The caller
 * that gets `null` draws the post the way it has always been drawn.
 */
export function readStyledPost(post: {record?: unknown}): StyledPost | null {
  const record = post?.record as Record<string, unknown> | undefined
  const parsed = styledPostSchema.safeParse(record?.[STYLIZER_MARK])
  return parsed.success ? parsed.data : null
}

/**
 * A mark as a value that can be written to a repo.
 *
 * `undefined` fields are dropped rather than written as absent keys: a record's
 * bytes are its identity on AT Protocol, so two equal marks have to serialize the
 * same way, and an explicit `{"texture": null}`-style absence is exactly the kind
 * of difference that makes two clients write different bytes for one meaning.
 */
export function styledPostValue(style: StyledPost): Record<string, unknown> {
  const value: Record<string, unknown> = {}
  for (const [key, entry] of Object.entries(style)) {
    if (entry !== undefined) value[key] = entry
  }
  return value
}
